import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { formatarValor } from '../../../core/utils/formatadores.util';

/** Limite de dígitos digitados (centavos inclusos), para o valor não perder precisão. */
const DIGITOS_MAXIMO = 15;

/**
 * Máscara de valor em reais para `<input>`: os dígitos entram pela direita, como centavos
 * ("1" → "0,01"; "123456" → "1.234,56"). A tela mostra só o número formatado (o "R$" fica num
 * span à parte) e o formulário recebe o `number` (ou `null` com o campo vazio).
 *
 * @example
 * <input type="text" inputmode="numeric" appMascaraMoeda formControlName="valor" />
 */
@Directive({
  selector: 'input[appMascaraMoeda]',
  standalone: true,
  host: {
    '(input)': 'aoDigitar()',
    '(blur)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MascaraMoedaDirective),
      multi: true,
    },
  ],
})
export class MascaraMoedaDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<HTMLInputElement>>(ElementRef).nativeElement;

  private onChange: (valor: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: number | null): void {
    this.elemento.value =
      valor === null || valor === undefined || !Number.isFinite(Number(valor))
        ? ''
        : formatarValor(Number(valor));
  }

  registerOnChange(fn: (valor: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoDigitar(): void {
    const digitos = this.elemento.value.replace(/\D/g, '').slice(0, DIGITOS_MAXIMO);
    if (!digitos) {
      this.elemento.value = '';
      this.onChange(null);
      return;
    }
    const valor = Number(digitos) / 100;
    this.elemento.value = formatarValor(valor);
    this.onChange(valor);
  }

  aoSair(): void {
    this.onTouched();
  }
}
